import { useState } from 'react'
import { Box, Button, Card, Field, Flex, Input, Stack, Tag } from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'
import { useEventTypes } from '../contexts/EventTypesContext'
import { eventPermissions } from '../contexts/EventWorkspaceContext'
import { useEventDraft } from '../hooks/useEventDraft'
import { visibleGroups } from './eventSettings/sections'
import { FoldableSection } from './FoldableSection'
import { toaster } from './ui/toaster'
import { BASE_CREATE_DEFAULTS, DEFAULT_TYPE } from '../constants/eventTypes'
import { useTranslation } from '../i18n'

/**
 * The create-event form on the events list. The same section components the
 * event's settings tabs use, stacked and all editable, limited to the ones that
 * make sense before the event exists (no codes, roles, templates or info sheet
 * — those act on a stored event). Everything else is set in the tabs after.
 */
export default function CreateEventForm({ eventType = DEFAULT_TYPE, onDone }) {
  const { t, lang } = useTranslation()
  const { isSysAdmin } = useAuth()
  const { getTypeConfig } = useEventTypes()
  const typeConfig = getTypeConfig(eventType)

  // Seeded once from the chosen type; the draft is the form from then on.
  const [defaults] = useState(() => ({
    ...BASE_CREATE_DEFAULTS,
    ...(typeConfig?.defaults || {}),
    eventType,
  }))
  const draft = useEventDraft({ eventId: null, stored: defaults, creating: true, lang })
  const permissions = eventPermissions({ typeConfig, isSysAdmin, eventRole: null })

  const s = {
    eventId: null,
    event: draft.draft,
    eventTypeId: eventType,
    typeConfig,
    typeAdminRole: permissions.typeAdminRole,
    eventRole: null,
    isSysAdmin,
    isHidden: permissions.isHidden,
    canEdit: true,
    editing: true,
    draft: draft.draft,
    setDraft: draft.setDraft,
    setField: draft.setField,
    setFile: draft.setFile,
    info: draft.info,
  }

  const handleCreate = async () => {
    try {
      await draft.save()
      toaster.create({ title: t('events.eventUpdated'), status: 'success' })
      onDone?.()
    } catch (err) {
      toaster.create({ title: t('events.eventUpdateFailed'), description: err.message, status: 'error' })
    }
  }

  return (
    <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
      <Card.Body>
        <Stack gap={5}>
          <Flex gap={3} align="flex-end" wrap="wrap">
            <Field.Root flex="1" minW="240px">
              <Field.Label>{t('events.eventName')}</Field.Label>
              <Input
                value={draft.draft?.name ?? ''}
                onChange={(e) => draft.setField('name', e.target.value)}
                placeholder={t('events.eventNameEditPlaceholder')}
                autoFocus
              />
            </Field.Root>
            {typeConfig && (
              <Tag.Root colorPalette={typeConfig.tagColor} mb={2}>
                {typeConfig.label?.[lang] || typeConfig.label?.en || eventType}
              </Tag.Root>
            )}
          </Flex>

          {visibleGroups(s, { creating: true }).flatMap((g) => g.sections).map((section) => (
            <FoldableSection key={section.id} title={t(section.labelKey)}>
              <Box pt={2}><section.Component s={s} /></Box>
            </FoldableSection>
          ))}

          <Flex justify="flex-end" gap={3}>
            <Button variant="outline" disabled={draft.saving} onClick={() => onDone?.()}>{t('common.cancel')}</Button>
            <Button
              colorPalette="brand"
              loading={draft.saving}
              loadingText={t('common.saving')}
              disabled={!draft.draft?.name?.trim()}
              onClick={handleCreate}
            >
              {t('events.createEvent')}
            </Button>
          </Flex>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}
